import { FormEvent, useContext, useState } from "react";

import { updateDeal, UpdateDealRequest } from "../../../../../../../service/fund";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { getWorkflowStageKey, useIsLoadingWrapper } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";
import { DealDatesRecord, parseDealDates, toStageLabel } from "../../utils";
import {
  COMPLETE_STATUS,
  getNextStageAfterCompletion,
  IN_PROGRESS_STATUS,
  setStageStatus,
} from "./utils/completeStageModalUtils";

interface UseCompleteStageModalReturnData {
  show: boolean;
  isHandleCompleteStageLoading: boolean;
  errors: ServiceError[] | undefined;
  handleClose: () => void;
  handleCompleteStage: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

const resolveNextStageValue = (
  currentStageValue: string | null | undefined,
  currentStageKey: string,
  currentStageLabel: string,
  nextStageKey: string,
  nextStageLabel: string
): string => {
  const currentStageKeyValue = getWorkflowStageKey(currentStageValue);

  if (currentStageKeyValue === currentStageKey) {
    return nextStageKey;
  }

  if (currentStageKeyValue === getWorkflowStageKey(currentStageLabel)) {
    return nextStageLabel;
  }

  return nextStageLabel;
};

export const useCompleteStageModal = (): UseCompleteStageModalReturnData => {
  const {
    deal,
    tasksBoard,
    dealStages,
    currentDealStage,
    showCompleteStageModal,
    setShowCompleteStageModal,
    handleDealStageChange,
    shouldRefreshTasksBoard,
  } = useContext(TasksBoardContext);

  const [errors, setErrors] = useState<ServiceError[] | undefined>(undefined);
  const [isHandleCompleteStageLoading, setIsHandleCompleteStageLoading] = useState(false);

  const handleClose = (): void => {
    setErrors([]);
    setShowCompleteStageModal(false);
  };

  const handleCompleteStage = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const dealUuid = tasksBoard?.results[0].dealUuid;
    if (!dealUuid || !currentDealStage) {
      setErrors([{ code: "DEAL_STAGE_NOT_FOUND", message: "Unable to determine the current deal stage." }]);
      return;
    }

    if (!deal) {
      setErrors([{ code: "DEAL_NOT_FOUND", message: "Unable to load the latest deal details." }]);
      return;
    }

    const dealDates = parseDealDates(deal.dealDates);
    if (!dealDates) {
      setErrors([{ code: "DEAL_DATES_INVALID", message: "Unable to read the deal stage dates." }]);
      return;
    }

    const stageKeys = dealStages.length ? dealStages.map((stage) => stage.key) : Object.keys(dealDates);
    const currentStageIndex = stageKeys.findIndex((stageKey) => stageKey === getWorkflowStageKey(currentDealStage));

    if (currentStageIndex < 0) {
      setErrors([{ code: "DEAL_STAGE_NOT_FOUND", message: "Unable to match the current deal stage." }]);
      return;
    }

    const currentStageKey = stageKeys[currentStageIndex];
    const currentStageLabel = toStageLabel(currentStageKey);

    const updatedDealDates: DealDatesRecord = { ...dealDates };
    updatedDealDates[currentStageKey] = setStageStatus(updatedDealDates[currentStageKey], COMPLETE_STATUS);
    const { nextStageKey, nextStageLabel, nextIncompleteStageKey, nextIncompleteStageLabel } =
      getNextStageAfterCompletion(stageKeys, currentStageIndex, updatedDealDates);

    if (nextIncompleteStageKey) {
      updatedDealDates[nextIncompleteStageKey] = setStageStatus(
        updatedDealDates[nextIncompleteStageKey],
        IN_PROGRESS_STATUS
      );
    }

    const targetStageKey = nextIncompleteStageKey ?? nextStageKey;
    const targetStageLabel = nextIncompleteStageLabel ?? nextStageLabel;

    const request: UpdateDealRequest = {
      conversionProbability: deal.conversionProbability,
      dealDates: updatedDealDates as unknown as UpdateDealRequest["dealDates"],
      dealSpecificData: deal.dealSpecificData as unknown as UpdateDealRequest["dealSpecificData"],
      dealTeam: deal.usersInvolved?.map((user) => user.uuid) ?? [],
      dealUuid: deal.uuid,
      description: deal.description ?? "",
      estimatedFees: deal.estimatedFees ?? 0,
      introductionDate: deal.introductionDate ?? null,
      IsTemplateSelected: undefined,
      leadUserUuid: deal.leadUser?.uuid ?? "",
      name: deal.name,
      projectedDpi: deal.projectedDpi ?? null,
      relatedObjects:
        deal.relatedObjects?.map((relatedObject) => ({
          objectType: relatedObject.objectType,
          objectUuid: relatedObject.objectUuid,
        })) ?? [],
      rowVersion: deal.rowVersion,
      source: deal.source ?? null,
      stage:
        targetStageKey && targetStageLabel
          ? resolveNextStageValue(deal.stage, currentStageKey, currentStageLabel, targetStageKey, targetStageLabel)
          : deal.stage,
      status: deal.status,
      targetCloseDate: deal.targetCloseDate ?? null,
    };

    const updateDealRes = await updateDeal(request);
    if (updateDealRes.status === Status.Success) {
      if (targetStageKey) {
        handleDealStageChange(targetStageKey);
      }
      Toast.success({ message: "Deal stage successfully completed" });
      shouldRefreshTasksBoard();
      handleClose();
      return;
    }

    setErrors(updateDealRes.errors);
  }, setIsHandleCompleteStageLoading);

  return {
    show: showCompleteStageModal,
    isHandleCompleteStageLoading,
    errors,
    handleClose,
    handleCompleteStage,
  };
};
